import { toast } from "react-toastify";
import { ROLE_HOME_MAP } from "../../../../features/bonds/shared/bondFormConfig";
import logger from "../../../../globals/utils/logger";

/** Role → home route (includes professional guardian for blanket historic). */
export const ROLE_PATH_MAP = ROLE_HOME_MAP;

export function navigateToRoleHome(navigate, role) {
  navigate(ROLE_PATH_MAP[role] || "/");
}

export function formatGuardianFieldLabel(fieldName, guardianErrorFieldLabels) {
  return guardianErrorFieldLabels[fieldName] || fieldName;
}

export function formatRequiredFieldLabel(field, guardianErrorFieldLabels) {
  if (field.startsWith("guardians.")) {
    const parts = field.split(".");
    const guardianIndex = Number.parseInt(parts[1], 10) + 1;
    const fieldName = parts[2];
    return `Principal ${guardianIndex} ${formatGuardianFieldLabel(
      fieldName,
      guardianErrorFieldLabels
    )}`;
  }
  return field
    .replace(/([A-Z])/g, " $1")
    .replaceAll(".", " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

export function buildHistoricPrincipalDisplayName(basicInformation) {
  return [basicInformation?.firstName, basicInformation?.lastName].filter(Boolean).join(" ");
}

export function buildHistoricBillingFields(billingInformation = {}) {
  return {
    billing_entity: billingInformation.billing_entity,
    billing_lawfirmaddress_name: billingInformation.billing_lawfirmaddress_name,
    billing_principal_name: billingInformation.billing_principal_name,
    billing_other_name: billingInformation.billing_other_name,
    billing_other_address: billingInformation.billing_other_address,
    billing_other_relationship: billingInformation.billing_other_relationship,
    billing_other_city: billingInformation.billing_other_city || "",
    billing_other_state: billingInformation.billing_other_state || "",
    billing_other_zip: billingInformation.billing_other_zip || "",
  };
}

export function buildHistoricDocumentFields(documentUpload = {}) {
  return {
    copy_of_court_order: documentUpload.courtOrderFileName,
    copy_of_court_order_link: documentUpload.courtOrderLink,
    other_document: documentUpload.otherDocumentFileName,
    other_document_link: documentUpload.otherDocumentLink,
    notes: documentUpload.note,
  };
}

export function buildHistoricDeclineFields(generalInformation = {}, { creditReport = false } = {}) {
  const fields = {
    is_declined: generalInformation.isDeclined === "Yes",
    is_declined_explanation: generalInformation.explanations?.isDeclined,
  };
  if (creditReport) {
    fields.credit_report_allowed = generalInformation.requestForAuthorization === "Yes";
  }
  return fields;
}

const FIDUCIARY_PREFIX_BY_KIND = {
  va: {
    operateBusiness: "fiduciary_operate_beneficiarys_business",
    indebted: "principal_indebted_to_estate",
    indebtedExplanation: "principal_indebted_explanation",
    lastWill: "beneficiary_execute_last_will_and_testament",
    lastWillExplanation: "beneficiary_execute_last_will_and_testament_explaination",
    replaceFiduciary: "principal_replace_prior_fiduciary",
    replaceFiduciaryExplanation: "principal_replace_prior_fiduciary_explaination",
  },
  guardianship: {
    operateBusiness: "fiduciary_operate_wards_business",
    indebted: "guardian_indebited_to_estate",
    indebtedExplanation: "guardian_indebted_explanation",
    lastWill: "ward_execute_last_will_and_testament",
    lastWillExplanation: "ward_execute_last_will_and_testament_explaination",
    replaceFiduciary: "guardian_replace_prior_fiduciary",
    replaceFiduciaryExplanation: "guardian_replace_prior_fiduciary_explaination",
  },
};

export function buildHistoricFiduciaryFields(globalState, kind) {
  const { generalInformation = {}, documentUpload = {} } = globalState;
  const prefix = FIDUCIARY_PREFIX_BY_KIND[kind];
  if (!prefix) return {};

  return {
    [prefix.operateBusiness]: generalInformation.ongoingBusiness === "Yes",
    fiduciary_business_explanation: generalInformation.explanations?.ongoingBusiness,
    [prefix.indebted]: generalInformation.indebtedGuardians === "Yes",
    [prefix.indebtedExplanation]: generalInformation.explanations?.indebtedGuardians,
    [prefix.lastWill]: generalInformation.didLastWillAndTestament === "Yes",
    copy_of_last_will_and_testament: documentUpload.lastWillFileName,
    copy_of_last_will_and_testament_link: documentUpload.lastWillLink,
    [prefix.replaceFiduciary]: generalInformation.replacingFiduciary === "Yes",
    bond_replace_prior_bond: generalInformation.replacingPriorBond === "Yes",
    replaced_by_non_court: generalInformation.demandedByInterestedParty === "Yes",
    replaced_non_court_explanation:
      generalInformation.explanations?.demandedByInterestedParty,
    joint_control_exercised: generalInformation.jointControlExercised === "Yes",
    supplemental_needs_trust_created: generalInformation.supplementalNeedsTrust === "Yes",
    copy_of_supplemental_needs_trust: documentUpload.otherDocumentFileName,
    copy_of_supplemental_needs_trust_link: documentUpload.otherDocumentLink,
    [prefix.lastWillExplanation]:
      generalInformation.explanations?.didLastWillAndTestament,
    [prefix.replaceFiduciaryExplanation]:
      generalInformation.explanations?.replacingFiduciary,
    bond_replace_prior_bond_explaination:
      generalInformation.explanations?.replacingPriorBond,
    required_non_court_explanation:
      generalInformation.explanations?.demandedByInterestedParty,
    joint_control_explanation: generalInformation.explanations?.jointControlExercised,
    supplemental_needs_trust_explanation:
      generalInformation.explanations?.supplementalNeedsTrust,
    is_bankrupt_insolvent: generalInformation.bankruptOrInsolvent === "Yes",
    is_bankrupt_insolvent_explanation:
      generalInformation.explanations?.bankruptOrInsolventExplanation,
    is_lawsuits_judgements_liens: generalInformation.outstandingLawsuit === "Yes",
    is_lawsuits_judgements_liens_explanation:
      generalInformation.explanations?.outstandingLawsuitExplanation,
  };
}

export function restoreResumedBondPayload({ reset, setActiveStep, logContext }) {
  const savedPayload = localStorage.getItem("resumedBondPayload");
  if (!savedPayload) return;
  try {
    const parsed = JSON.parse(savedPayload);
    let body = parsed.request_body;
    if (typeof body === "string") body = JSON.parse(body);
    if (body?.state) reset(body.state);
    if (typeof body?.activeStep === "number") setActiveStep(body.activeStep);
    toast.info("Restored bond application from support request.");
    localStorage.removeItem("resumedBondPayload");
  } catch (err) {
    logger.debug(logContext, "resume payload parse failed", { err });
  }
}

export function storeBondFlashToast(response, updateRoute) {
  try {
    const bondId =
      response?.bond_id ||
      response?.id ||
      response?.bond?.bond_id ||
      response?.data?.bond_id;
    if (!bondId) return;
    sessionStorage.setItem(
      "flashToast",
      JSON.stringify({
        message: `Your Recent Bond Request #${bondId} was Submitted!`,
        link: updateRoute.replace(":bondId", bondId).replace("{bondId}", bondId),
      })
    );
  } catch (err) {
    logger.debug("storeBondFlashToast", "sessionStorage failed", { err });
  }
}
